import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { compare } from 'fast-json-patch'
import { isPossiblePhoneNumber } from 'react-phone-number-input'

import { useUpdateProfile } from '../../../../../hooks/profile/useUpdateProfile'

import FormSection from '../../../../formSection/FormSection'
import FormSectionActions from '../../../../formSection/formSectionActions/FormSectionActions'
import { TextInput } from '../../../../input/TextInput'
import { SelectOptionInput } from '../../../../input/selectOptionInput/SelectOptionInput'
import { PhoneNumberInput } from '../../../../input/phoneNumberInput/PhoneNumberInput'

import { useApp } from '../../../../../contexts/AppContext'
import { useProfileContext } from '../../../../../contexts/ProfileContext'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import {
  DominantHand,
  ProfileDetailsModel,
  SKILL_LEVEL_VALUES,
  SkillLevel,
  UserProfile,
} from '../../../../../types/user/userProfile'
import { InputType } from '../../../../../constants/inputTypes'
import { REQUIRED_PROFILE_DETAILS_FIELDS } from '../../../../../utils/profile/profile'

type ProfileDetailsProps = {
  profile: UserProfile | null
  userEmail: string
  requiredOnly?: boolean
  onSaveSuccess?: () => void
  onSkip?: () => void
  saveLabel?: string
  skipLabel?: string
}

const PROFILE_DETAILS_FIELDS: (keyof ProfileDetailsModel)[] = [
  'nickname',
  'firstName',
  'lastName',
  'phoneNumber',
  'skillLevel',
  'dominantHand',
]

const getFieldsValidity = (
  details: ProfileDetailsModel
): Record<keyof ProfileDetailsModel, boolean> => ({
  nickname: details.nickname.trim().length > 0,
  firstName: details.firstName.trim().length > 0,
  lastName: details.lastName.trim().length > 0,
  phoneNumber:
    details.phoneNumber.trim().length === 0 ||
    isPossiblePhoneNumber(details.phoneNumber),
  skillLevel:
    details.skillLevel === null ||
    Object.values(SkillLevel).includes(details.skillLevel),
  dominantHand:
    details.dominantHand === null ||
    Object.values(DominantHand).includes(details.dominantHand),
})

const toProfileDetailsModel = (
  profile?: Partial<UserProfile> | null
): ProfileDetailsModel => ({
  firstName: profile?.firstName ?? '',
  lastName: profile?.lastName ?? '',
  nickname: profile?.nickname ?? '',
  phoneNumber: profile?.phoneNumber ?? '',
  skillLevel: profile?.skillLevel ?? null,
  dominantHand: profile?.dominantHand ?? null,
})

const ProfileDetails = ({
  profile,
  userEmail,
  requiredOnly = false,
  onSaveSuccess,
  onSkip,
  saveLabel,
  skipLabel,
}: ProfileDetailsProps) => {
  const { showBanner } = useApp()
  const { updateLocalProfile } = useProfileContext()
  const { t } = useTranslation('account')

  const { updateProfile, loading, error, success, resetState } =
    useUpdateProfile()

  useEffect(() => {
    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }

    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
    }

    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, success])

  const [details, setDetails] = useState<ProfileDetailsModel>(
    toProfileDetailsModel()
  )
  const [initialDetails, setInitialDetails] =
    useState<ProfileDetailsModel | null>(null)
  const [fieldsValidity, setFieldsValidity] = useState<
    Record<keyof ProfileDetailsModel, boolean>
  >(() => getFieldsValidity(toProfileDetailsModel()))
  useEffect(() => {
    if (!profile) {
      return
    }

    const model = toProfileDetailsModel(profile)
    setInitialDetails(model)
    setDetails(model)
    setFieldsValidity(getFieldsValidity(model))
  }, [profile])

  const handleChange = (
    name: string,
    value: string | boolean | File | null,
    isValid = true
  ) => {
    if (
      typeof value !== 'string' ||
      !PROFILE_DETAILS_FIELDS.includes(name as keyof ProfileDetailsModel)
    ) {
      return
    }

    const field = name as keyof ProfileDetailsModel
    const normalizedValue =
      (field === 'skillLevel' || field === 'dominantHand') && value === ''
        ? null
        : value

    setDetails(prev => ({
      ...prev,
      [name]: normalizedValue,
    }))

    const fieldIsValid =
      field === 'skillLevel'
        ? value === '' || Object.values(SkillLevel).includes(value as SkillLevel)
        : field === 'dominantHand'
          ? value === '' ||
            Object.values(DominantHand).includes(value as DominantHand)
          : field === 'phoneNumber'
            ? isValid
          : isValid && value.trim().length > 0

    setFieldsValidity(previous => ({
      ...previous,
      [field]: fieldIsValid,
    }))
  }

  const patch = useMemo(
    () => (initialDetails ? compare(initialDetails, details) : []),
    [details, initialDetails]
  )

  const patchIsValid = patch.every(operation => {
    const field = operation.path.slice(1) as keyof ProfileDetailsModel

    return PROFILE_DETAILS_FIELDS.includes(field) && fieldsValidity[field]
  })
  const fieldsToValidate: readonly (keyof ProfileDetailsModel)[] = requiredOnly
    ? REQUIRED_PROFILE_DETAILS_FIELDS
    : PROFILE_DETAILS_FIELDS
  const formIsValid = fieldsToValidate.every(field => fieldsValidity[field])

  const handleSave = async () => {
    if (!patchIsValid || !formIsValid || patch.length === 0 || loading) {
      return
    }

    const updatedProfile = await updateProfile(
      patch,
      t('account.hook.profile-details.saved')
    )

    if (!updatedProfile) {
      return
    }

    const normalizedDetails = toProfileDetailsModel(updatedProfile)

    updateLocalProfile(normalizedDetails)
    setDetails(normalizedDetails)
    setInitialDetails(normalizedDetails)
    setFieldsValidity(getFieldsValidity(normalizedDetails))
    onSaveSuccess?.()
  }

  const handleReset = () => {
    if (!initialDetails || loading) {
      return
    }

    setDetails(initialDetails)
    setFieldsValidity(getFieldsValidity(initialDetails))
  }

  const skillLevelOptions = Object.values(SkillLevel).map(level => ({
    value: level,
    label: t('account.profile.skill-level.value', {
      level: SKILL_LEVEL_VALUES[level],
    }),
  }))

  const dominantHandOptions = Object.values(DominantHand).map(hand => ({
    value: hand,
    label: t(`account.profile.hand.${hand.toLowerCase()}`),
  }))

  return (
    <FormSection
      title={t('account.profile.details.title')}
      subtitle={t('account.profile.details.subtitle')}
      actions={
        <FormSectionActions
          isDirty={patch.length > 0}
          isValid={patchIsValid && formIsValid}
          loading={loading}
          showReset={!onSaveSuccess || Boolean(onSkip)}
          enableSecondaryActionWhenUnchanged={Boolean(onSkip)}
          onSave={handleSave}
          onCancel={onSkip ?? handleReset}
          saveLabel={saveLabel ?? t('account.save-btn')}
          resetLabel={skipLabel ?? t('account.reset-btn')}
        />
      }
    >
      <div className="row g-3">
        <div className="col-12">
          <TextInput
            name="nickname"
            label={t('account.profile.nickname')}
            value={details.nickname}
            infoMessage={t('account.profile.nickname-info')}
            errorMessage={t('account.profile.validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <TextInput
            name="firstName"
            label={t('account.profile.first-name')}
            value={details.firstName}
            errorMessage={t('account.profile.validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <TextInput
            name="lastName"
            label={t('account.profile.last-name')}
            value={details.lastName}
            errorMessage={t('account.profile.validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        {!requiredOnly && (
          <>
            <div className="col-12 col-md-6">
              <TextInput
                name="email"
                type={InputType.Email}
                label={t('account.profile.email')}
                value={userEmail}
                disabled={true}
                onChange={() => {}}
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <PhoneNumberInput
                name="phoneNumber"
                label={t('account.profile.phone')}
                value={details.phoneNumber}
                errorMessage={t('account.profile.validation.phone')}
                onChange={handleChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <SelectOptionInput
                name="skillLevel"
                label={t('account.profile.skill-level')}
                value={details.skillLevel ?? ''}
                list={skillLevelOptions}
                onChange={handleChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <SelectOptionInput
                name="dominantHand"
                label={t('account.profile.dominant-hand')}
                value={details.dominantHand ?? ''}
                list={dominantHandOptions}
                onChange={handleChange}
              />
            </div>
          </>
        )}
      </div>
    </FormSection>
  )
}

export default ProfileDetails
